import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { customerAPI, ordersAPI } from '../services/api';
import { customToast, toastMessages } from '../utils/toast';
import {
  ShoppingCart,
  Plus,
  Minus,
  CheckCircle,
  Clock,
  Sparkles,
  Search,
  Utensils,
  X,
  Edit3,
  ChefHat,
  Package,
  CheckSquare,
  MapPin,
  AlertTriangle,
  Sun,
  Moon,
  Globe,
  ChevronDown,
  Check,
} from 'lucide-react';

// Import translation files directly
import enTranslations from '../locales/en.json';
import frTranslations from '../locales/fr.json';
import nlTranslations from '../locales/nl.json';
import deChTranslations from '../locales/de-ch.json';

// Create a standalone language context for this component
const CustomerLanguageContext = createContext();

// Available languages configuration
const customerLanguages = {
  en: {
    code: 'en',
    name: 'English',
    flag: '',
    translations: enTranslations
  },
  fr: {
    code: 'fr',
    name: 'Français',
    flag: '',
    translations: frTranslations
  },
  nl: {
    code: 'nl',
    name: 'Nederlands',
    flag: '',
    translations: nlTranslations
  },
  'de-ch': {
    code: 'de-ch',
    name: 'Schweizerdeutsch',
    flag: '',
    translations: deChTranslations
  }
};

// Custom hook for customer language
const useCustomerLanguage = () => {
  const context = useContext(CustomerLanguageContext);
  if (!context) {
    throw new Error('useCustomerLanguage must be used within CustomerLanguageProvider');
  }
  return context;
};

// Language Provider for Customer Menu
const CustomerLanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState(enTranslations);

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('customer_language');
    if (savedLanguage && customerLanguages[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
      setTranslations(customerLanguages[savedLanguage].translations);
    } else {
      // Try to detect browser language
      const browserLanguage = navigator.language.split('-')[0];
      if (customerLanguages[browserLanguage]) {
        setCurrentLanguage(browserLanguage);
        setTranslations(customerLanguages[browserLanguage].translations);
      }
    }
  }, []);

  // Change language function
  const changeLanguage = (languageCode) => {
    if (customerLanguages[languageCode]) {
      setCurrentLanguage(languageCode);
      setTranslations(customerLanguages[languageCode].translations);
      localStorage.setItem('customer_language', languageCode);
    }
  };

  // Translation function
  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return fallback || key;
      }
    }

    return typeof value === 'string' ? value : fallback || key;
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return customerLanguages[currentLanguage];
  };

  // Get all available languages
  const getAvailableLanguages = () => {
    return Object.values(customerLanguages);
  };

  const value = {
    currentLanguage,
    translations,
    changeLanguage,
    t,
    getCurrentLanguageInfo,
    getAvailableLanguages
  };

  return (
    <CustomerLanguageContext.Provider value={value}>
      {children}
    </CustomerLanguageContext.Provider>
  );
};

// Language Switcher Component for Customer Menu
const CustomerLanguageSwitcher = ({ variant = 'default' }) => {
  const { currentLanguage, changeLanguage, getCurrentLanguageInfo, getAvailableLanguages } = useCustomerLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = getCurrentLanguageInfo();
  const availableLanguages = getAvailableLanguages();

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setIsOpen(false);
  };

  if (variant === 'minimal') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          <span className="text-lg">{currentLang.flag}</span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{currentLang.code.toUpperCase()}</span>
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-20">
              {availableLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300">{lang.name}</span>
                  {currentLanguage === lang.code && (
                    <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
      >
        <Globe className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{currentLang.name}</span>
        <span className="text-lg">{currentLang.flag}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
          }`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-20">
            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Select Language</h3>
            </div>
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 ${currentLanguage === lang.code ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
              >
                <span className="text-xl">{lang.flag}</span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{lang.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{lang.code.toUpperCase()}</div>
                </div>
                {currentLanguage === lang.code && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Main Customer Menu Content (without the provider)
const CustomerMenuContent = () => {
  const { qrCode } = useParams();
  const navigate = useNavigate();
  const { t } = useCustomerLanguage();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [showSuggestionPopup, setShowSuggestionPopup] = useState(false);
  const [suggestedDishes, setSuggestedDishes] = useState([]);
  const [LastOrderedCategory, setLastOrderedCategory] = useState();
  const [HasShownSuggestions, setHasShownSuggestions] = useState();
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [suggestedHistory, setSuggestedHistory] = useState(new Set());

  // ============= NEW: Suggestion Tracking State =============
  const [suggestionSession, setSuggestionSession] = useState({
    sessionId: null,
    events: [],
    stats: {
      totalShown: 0,
      accepted: 0,
      ignored: 0,
      addedItems: []
    }
  });
  // =========================================================

  // Theme state with localStorage persistence
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  // Load cart from localStorage on component mount
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem(`cart_${qrCode}`);
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [showCart, setShowCart] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [customizationInputs, setCustomizationInputs] = useState({});
  const [activeTab, setActiveTab] = useState('menu');
  const [cancelModal, setCancelModal] = useState({ isOpen: false, order: null });
  const [orderedCategories, setOrderedCategories] = useState([]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Save theme to localStorage and apply to document
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.className = theme;
  }, [theme]);

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem(`cart_${qrCode}`, JSON.stringify(cart));
  }, [cart, qrCode]);

  // Toggle theme function
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // Fetch menu data
  const { data: menuData, isLoading, error } = useQuery(
    ['menu', qrCode],
    () => customerAPI.getMenu(qrCode),
    {
      enabled: !!qrCode,
      refetchInterval: 30000,
    }
  );

  // Fetch order tracking data
  const { data: ordersData, refetch: refetchOrders } = useQuery(
    ['orders', qrCode],
    () => customerAPI.getOrders(qrCode),
    {
      enabled: !!qrCode,
      refetchInterval: 2500,
      onSuccess: (data) => {
        console.log('Full orders response:', data);
        console.log('Orders data structure:', data?.data);
        console.log('Orders array:', data?.data?.orders);
      },
      onError: (error) => {
        console.error('Error fetching orders:', error);
      }
    }
  );

  // FIXED: Filter orders - hide completed after 30 seconds and cancelled after 5 seconds
  const orders = React.useMemo(() => {
    if (!ordersData) return [];

    // Try different possible structures
    let ordersArray = [];
    if (Array.isArray(ordersData)) {
      ordersArray = ordersData;
    } else if (Array.isArray(ordersData?.data)) {
      ordersArray = ordersData.data;
    } else if (Array.isArray(ordersData?.data?.orders)) {
      ordersArray = ordersData.data.orders;
    } else if (ordersData?.orders) {
      ordersArray = ordersData.orders;
    } else {
      console.warn('Unexpected orders data structure:', ordersData);
      return [];
    }

    const now = new Date();
    const COMPLETED_HIDE_DELAY_MS = 30000; // 30 seconds for completed orders
    const CANCELLED_HIDE_DELAY_MS = 5000;  // 5 seconds for cancelled orders

    return ordersArray.filter(order => {
      // Keep orders that are not completed or cancelled
      if (order.status !== 'completed' && order.status !== 'cancelled') {
        return true;
      }

      // For completed orders, check if they're within the visibility window
      if (order.status === 'completed') {
        const timestamp = order.completed_at || order.updated_at || order.created_at;
        if (timestamp) {
          const completedTime = new Date(timestamp);
          if (!isNaN(completedTime.getTime())) {
            const timeDiff = now - completedTime;
            return timeDiff < COMPLETED_HIDE_DELAY_MS;
          }
        }
        return false; // No valid timestamp, hide it
      }

      // For cancelled orders, show them briefly then hide
      if (order.status === 'cancelled') {
        const timestamp = order.cancelled_at || order.updated_at || order.created_at;
        if (timestamp) {
          const cancelledTime = new Date(timestamp);
          if (!isNaN(cancelledTime.getTime())) {
            const timeDiff = now - cancelledTime;
            return timeDiff < CANCELLED_HIDE_DELAY_MS;
          }
        }
        return false; // No valid timestamp, hide it
      }

      return true;
    });
  }, [ordersData]);

  // FIXED: Cleanup timer for completed and cancelled orders
  useEffect(() => {
    if (!ordersData) return;

    // Find the earliest completed or cancelled order that needs to be hidden
    const now = new Date();
    const COMPLETED_HIDE_DELAY_MS = 30000;
    const CANCELLED_HIDE_DELAY_MS = 5000;

    let nextExpiryTime = null;

    // Helper function to process orders
    const processOrder = (order, delayMs) => {
      const timestamp = order.completed_at || order.cancelled_at || order.updated_at || order.created_at;
      if (timestamp) {
        const orderTime = new Date(timestamp);
        if (!isNaN(orderTime.getTime())) {
          const expiryTime = orderTime.getTime() + delayMs;
          if (expiryTime > now.getTime()) {
            if (nextExpiryTime === null || expiryTime < nextExpiryTime) {
              nextExpiryTime = expiryTime;
            }
          }
        }
      }
    };

    // Extract orders array from the response structure
    let ordersArray = [];
    if (Array.isArray(ordersData)) {
      ordersArray = ordersData;
    } else if (Array.isArray(ordersData?.data)) {
      ordersArray = ordersData.data;
    } else if (Array.isArray(ordersData?.data?.orders)) {
      ordersArray = ordersData.data.orders;
    } else if (ordersData?.orders) {
      ordersArray = ordersData.orders;
    }

    // Check each completed and cancelled order
    ordersArray.forEach(order => {
      if (order.status === 'completed') {
        processOrder(order, COMPLETED_HIDE_DELAY_MS);
      } else if (order.status === 'cancelled') {
        processOrder(order, CANCELLED_HIDE_DELAY_MS);
      }
    });

    // Set timeout to refetch when the next order expires
    if (nextExpiryTime) {
      const timeUntilExpiry = nextExpiryTime - now.getTime();
      if (timeUntilExpiry > 0 && timeUntilExpiry < 60000) { // Only set if less than 60 seconds
        console.log(`Setting timeout for ${timeUntilExpiry}ms to refresh orders`);
        const timeoutId = setTimeout(() => {
          refetchOrders();
        }, timeUntilExpiry);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [ordersData, refetchOrders]);

  // FIXED: Add interval to regularly clean up old orders (every 5 seconds)
  useEffect(() => {
    if (!ordersData) return;

    const intervalId = setInterval(() => {
      refetchOrders();
    }, 5000); // Check every 5 seconds for expired orders

    return () => clearInterval(intervalId);
  }, [refetchOrders]);

  // ============= NEW: Suggestion Tracking Functions =============
  const generateSessionId = () => {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const trackSuggestionEvent = (type, dish = null, category = null) => {
    const event = {
      type,
      dish_id: dish?.id,
      dish_name: dish?.name,
      dish_price: dish?.price,
      category,
      timestamp: new Date().toISOString()
    };

    setSuggestionSession(prev => ({
      ...prev,
      events: [...prev.events, event]
    }));

    // Update stats
    if (type === 'accepted') {
      setSuggestionSession(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          accepted: prev.stats.accepted + 1,
          addedItems: [...prev.stats.addedItems, {
            dish_id: dish?.id,
            name: dish?.name,
            price: dish?.price,
            timestamp: new Date().toISOString()
          }]
        }
      }));
    } else if (type === 'ignored') {
      setSuggestionSession(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          ignored: prev.stats.ignored + 1
        }
      }));
    } else if (type === 'shown') {
      setSuggestionSession(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          totalShown: prev.stats.totalShown + 1
        }
      }));
    }

    return event;
  };

  const sendSuggestionStatsToServer = async () => {
    if (suggestionSession.stats.totalShown === 0 &&
      suggestionSession.stats.accepted === 0 &&
      suggestionSession.stats.ignored === 0) {
      return; // Don't send empty stats
    }

    try {
      await customerAPI.trackSuggestionStats({
        qr_code: qrCode,
        restaurant_id: menu?.restaurant?.id,
        table_id: menu?.table?.id,
        session_id: suggestionSession.sessionId,
        stats: suggestionSession.stats,
        events: suggestionSession.events,
        timestamp: new Date().toISOString()
      });

      console.log('Suggestion stats sent successfully');
    } catch (error) {
      console.error('Failed to track suggestion stats:', error);
      // You could store in localStorage to retry later
      const failedStats = JSON.parse(localStorage.getItem('failedSuggestionStats') || '[]');
      failedStats.push({
        restaurant_id: menu?.restaurant?.id,
        stats: suggestionSession.stats,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem('failedSuggestionStats', JSON.stringify(failedStats));
    }
  };
  // =========================================================

  // Create order mutation
  const createOrderMutation = useMutation(
    (orderData) => customerAPI.createOrder(orderData),
    {
      onSuccess: () => {
        customToast.success(toastMessages.orderPlaced);
      },
      onError: (error) => {
        customToast.error(toastMessages.createError('order'));
        setIsProcessingOrder(false);
      },
    }
  );

  // Add this useEffect to clean up when component unmounts or cart changes
  useEffect(() => {
    return () => {
      setIsProcessingOrder(false);
    };
  }, []);

  // Reset processing state when cart becomes empty
  useEffect(() => {
    if (cart.length === 0) {
      setIsProcessingOrder(false);
    }
  }, [cart]);

  // FIXED: Cancel order mutation with immediate refresh
  const cancelOrderMutation = useMutation(
    (orderId) => customerAPI.cancelOrder({ id: orderId, qr_code: qrCode }),
    {
      onSuccess: (response) => {
        console.log('Cancel order success response:', response);
        customToast.success(toastMessages.orderCancelled);

        // Immediately refetch orders to show cancelled status
        refetchOrders();

        // Close the modal
        setCancelModal({ isOpen: false, order: null });

        // Set a timeout to refresh again after 5 seconds to remove cancelled order
        setTimeout(() => {
          refetchOrders();
        }, 5000);
      },
      onError: (error) => {
        console.error('Cancel order error details:', error);
        customToast.error(toastMessages.cancelError('order'));
        setCancelModal({ isOpen: false, order: null });
      },
    }
  );

  // Function to get suggestions based on ordered items
  const getSuggestions = (orderedItems, allCategories, previousSuggestions = new Set()) => {
    const suggestions = [];

    // Get categories from ordered items
    const orderedCategories = new Set();
    const orderedDishIds = new Set(orderedItems.map(item => item.dish_id));

    orderedItems.forEach(item => {
      const dishCategory = allCategories.find(cat =>
        cat.dishes.some(dish => dish.id === item.dish_id)
      );
      if (dishCategory) {
        orderedCategories.add(dishCategory.id);
        setLastOrderedCategory(dishCategory.id);
      }
    });

    // Helper to check if dish was already suggested
    const wasAlreadySuggested = (dishId) => {
      return previousSuggestions.has(dishId) || suggestedHistory.has(dishId);
    };

    // Strategy 1: Suggest desserts if main courses were ordered
    const mainCourseCategories = allCategories.filter(cat =>
      cat.name.toLowerCase().includes('main') ||
      cat.name.toLowerCase().includes('entree') ||
      cat.name.toLowerCase().includes('plats')
    );

    const dessertCategories = allCategories.filter(cat =>
      cat.name.toLowerCase().includes('dessert') ||
      cat.name.toLowerCase().includes('sweet') ||
      cat.name.toLowerCase().includes('dolce')
    );

    const drinkCategories = allCategories.filter(cat =>
      cat.name.toLowerCase().includes('drink') ||
      cat.name.toLowerCase().includes('beverage') ||
      cat.name.toLowerCase().includes('boisson') ||
      cat.name.toLowerCase().includes('drank') ||
      cat.name.toLowerCase().includes('getränk')
    );

    const hasMainCourse = Array.from(orderedCategories).some(catId => {
      const category = allCategories.find(c => c.id === catId);
      return mainCourseCategories.some(mainCat => mainCat.id === category?.id);
    });

    if (hasMainCourse) {
      dessertCategories.forEach(cat => {
        const availableDesserts = cat.dishes.filter(dish =>
          dish.available &&
          !orderedDishIds.has(dish.id) &&
          !wasAlreadySuggested(dish.id)
        );
        // Only add desserts that haven't been suggested before
        const newDesserts = availableDesserts.slice(0, 2);
        suggestions.push(...newDesserts);
      });
    }

    // Strategy 2: Suggest drinks (only if not already suggested)
    drinkCategories.forEach(cat => {
      const availableDrinks = cat.dishes.filter(dish =>
        dish.available &&
        !orderedDishIds.has(dish.id) &&
        !wasAlreadySuggested(dish.id)
      );
      const newDrinks = availableDrinks.slice(0, 2);
      suggestions.push(...newDrinks);
    });

    // Strategy 3: Suggest popular items from other categories
    const otherCategories = allCategories.filter(cat =>
      !Array.from(orderedCategories).includes(cat.id) &&
      !dessertCategories.some(dc => dc.id === cat.id) &&
      !drinkCategories.some(dc => dc.id === cat.id)
    );

    otherCategories.forEach(cat => {
      const popularItems = cat.dishes
        .filter(dish =>
          dish.available &&
          dish.popular &&
          !orderedDishIds.has(dish.id) &&
          !wasAlreadySuggested(dish.id)
        )
        .slice(0, 1);
      suggestions.push(...popularItems);
    });

    // Strategy 4: If no specific suggestions, get random dishes not suggested before
    if (suggestions.length < 3) {
      const allAvailableDishes = allCategories.flatMap(cat =>
        cat.dishes.filter(dish =>
          dish.available &&
          !orderedDishIds.has(dish.id) &&
          !wasAlreadySuggested(dish.id)
        )
      );

      const randomDishes = allAvailableDishes
        .sort(() => Math.random() - 0.5)
        .slice(0, 3 - suggestions.length);

      suggestions.push(...randomDishes);
    }

    // Remove duplicates and limit to 3 suggestions
    const uniqueSuggestions = suggestions.filter((dish, index, self) =>
      index === self.findIndex(d => d.id === dish.id)
    ).slice(0, 3);

    // Update suggestion history
    uniqueSuggestions.forEach(dish => {
      setSuggestedHistory(prev => new Set([...prev, dish.id]));
    });

    return uniqueSuggestions;
  };

  // Simple translation function with fallback
  const safeTranslate = (key, fallback) => {
    const result = t(key);
    return result === key ? fallback : result;
  };

  const menu = menuData?.data;
  const restaurant = menu?.restaurant;
  const table = menu?.table;
  const categories = menu?.categories || [];

  // Fetch and apply category order
  useEffect(() => {
    const applyCategoryOrder = async () => {
      if (!menu?.restaurant?.id) return;

      try {
        const response = await customerAPI.getCategoryOrder?.(menu.restaurant.id);
        const savedOrder = response?.category_order || [];

        if (savedOrder.length > 0 && categories.length > 0) {
          // Order categories according to saved order
          const ordered = savedOrder
            .map(id => categories.find(cat => cat.id === id))
            .filter(cat => cat); // Remove any null values

          // Add any new categories not in the saved order at the end
          const remainingCategories = categories.filter(
            cat => !savedOrder.includes(cat.id)
          );

          setOrderedCategories([...ordered, ...remainingCategories]);
        } else {
          setOrderedCategories(categories);
        }
      } catch (error) {
        console.error('Error applying category order:', error);
        setOrderedCategories(categories);
      }
    };

    applyCategoryOrder();
  }, [categories, menu?.restaurant?.id]);

  // Order statuses with translations
  const orderStatuses = [
    {
      key: 'pending',
      label: safeTranslate('customerMenu.orderTracking.status.pending', 'Order Placed'),
      icon: CheckSquare,
      color: 'bg-blue-500'
    },
    {
      key: 'in_progress',
      label: safeTranslate('customerMenu.orderTracking.status.inProgress', 'Preparing'),
      icon: ChefHat,
      color: 'bg-orange-500'
    },
    {
      key: 'completed',
      label: safeTranslate('customerMenu.orderTracking.status.completed', 'Completed'),
      icon: CheckCircle,
      color: 'bg-green-500'
    }
  ];

  // Status messages with translations
  const getStatusMessage = (status) => {
    const messages = {
      pending: safeTranslate('customerMenu.orderTracking.status.messages.pending', "We've received your order and will start preparing it soon."),
      in_progress: safeTranslate('customerMenu.orderTracking.status.messages.inProgress', "Your food is being prepared with care by our chefs."),
      completed: safeTranslate('customerMenu.orderTracking.status.messages.completed', "Your order is ready! Enjoy your meal!"),
      cancelled: safeTranslate('customerMenu.orderTracking.status.messages.cancelled', "This order has been cancelled.")
    };
    return messages[status] || safeTranslate('customerMenu.orderTracking.tracking', "Tracking your order...");
  };

  // Filter dishes based on search
  const filteredCategories = orderedCategories.map(category => ({
    ...category,
    dishes: category.dishes.filter(dish =>
      dish.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dish.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.dishes.length > 0);

  const addToCart = (dish) => {
    const existingItem = cart.find(item => item.dish_id === dish.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.dish_id === dish.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        dish_id: dish.id,
        name: dish.name,
        price: dish.price,
        quantity: 1,
        customizations: '',
        image_url: dish.image_url
      }]);
    }
    customToast.success(toastMessages.itemAdded(dish.name));
  };

  const removeFromCart = (dishId) => {
    setCart(cart.filter(item => item.dish_id !== dishId));
    // Clear customization input for removed item
    setCustomizationInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[dishId];
      return newInputs;
    });
    customToast.success(toastMessages.itemRemoved);
  };

  const updateQuantity = (dishId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(dishId);
      return;
    }
    setCart(cart.map(item =>
      item.dish_id === dishId
        ? { ...item, quantity }
        : item
    ));
  };

  const updateCustomization = (dishId, customizations) => {
    setCart(cart.map(item =>
      item.dish_id === dishId
        ? { ...item, customizations }
        : item
    ));
  };

  const handleCustomizationChange = (dishId, value) => {
    setCustomizationInputs(prev => ({
      ...prev,
      [dishId]: value
    }));
    updateCustomization(dishId, value);
  };

  const getTotalPrice = () => {
    if (!cart || !Array.isArray(cart)) return 0;

    return cart.reduce((total, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQuantity = parseInt(item.quantity) || 0;
      return total + (itemPrice * itemQuantity);
    }, 0);
  };

  const getTotalItems = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  // ============= UPDATED: handleAddSuggestion with Tracking =============
  const handleAddSuggestion = (dish) => {
    // Add to cart
    const existingItem = cart.find(item => item.dish_id === dish.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.dish_id === dish.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        dish_id: dish.id,
        name: dish.name,
        price: parseFloat(dish.price || 0),
        quantity: 1,
        customizations: '',
        image_url: dish.image_url
      }]);
    }

    customToast.success(toastMessages.itemAdded(dish.name));

    // Track accepted suggestion
    const category = categories.find(cat =>
      cat.dishes.some(d => d.id === dish.id)
    )?.name;
    trackSuggestionEvent('accepted', dish, category);

    // Remove from suggestions
    const remainingSuggestions = suggestedDishes.filter(d => d.id !== dish.id);
    setSuggestedDishes(remainingSuggestions);

    // Auto-close if no more suggestions and proceed
    if (remainingSuggestions.length === 0) {
      setShowSuggestionPopup(false);
      // Send stats to server
      sendSuggestionStatsToServer();
      setTimeout(() => {
        proceedWithOrder();
      }, 500);
    }
  };
  // =========================================================

  // Handle closing suggestion popup
  const handleCloseSuggestionPopup = () => {
    setShowSuggestionPopup(false);
    setSuggestedDishes([]);
    // Send stats before proceeding
    sendSuggestionStatsToServer();
    proceedWithOrder();
  };

  // ============= UPDATED: handleSkipSuggestions with Tracking =============
  const handleSkipSuggestions = () => {
    // Track ignored suggestions
    suggestedDishes.forEach(dish => {
      const category = categories.find(cat =>
        cat.dishes.some(d => d.id === dish.id)
      )?.name;
      trackSuggestionEvent('ignored', dish, category);
    });

    setShowSuggestionPopup(false);
    setSuggestedDishes([]);

    // Send stats to server
    sendSuggestionStatsToServer();
    proceedWithOrder();
  };

  const handleClosePop = () => {
    setShowSuggestionPopup(false);
  };

  useEffect(() => {
    if (cart.length === 0) {
      setHasShownSuggestions(false);
    }
  }, [cart]);

  useEffect(() => {
    if (cart.length === 0) {
      setSuggestedHistory(new Set());
      setHasShownSuggestions(false);
    }
  }, [cart]);

  // ============= UPDATED: handlePlaceOrder with Tracking =============
  const handlePlaceOrder = () => {
    if (cart.length === 0) {
      customToast.error(toastMessages.cartEmpty);
      return;
    }

    // Close cart immediately
    setShowCart(false);

    // Prevent multiple clicks
    if (isProcessingOrder) return;

    setIsProcessingOrder(true);

    // Start new suggestion tracking session
    const sessionId = generateSessionId();
    setSuggestionSession({
      sessionId,
      events: [],
      stats: {
        totalShown: 0,
        accepted: 0,
        ignored: 0,
        addedItems: []
      }
    });

    // Get suggestions with history tracking
    const suggestions = getSuggestions(cart, categories, suggestedHistory);

    // Only show popup if we have suggestions and not already showing
    if (suggestions.length > 0 && !showSuggestionPopup) {
      // Track that suggestions were shown
      suggestions.forEach(dish => {
        const category = categories.find(cat =>
          cat.dishes.some(d => d.id === dish.id)
        )?.name;
        trackSuggestionEvent('shown', dish, category);
      });

      setSuggestedDishes(suggestions);
      setShowSuggestionPopup(true);
    } else {
      // No suggestions, proceed directly
      sendSuggestionStatsToServer();
      proceedWithOrder();
    }
  };

  const proceedWithOrder = () => {
    if (cart.length === 0) {
      customToast.error(toastMessages.cartEmpty);
      setIsProcessingOrder(false);
      return;
    }

    createOrderMutation.mutate({
      qr_code: qrCode,
      items: cart.map(item => ({
        dish_id: item.dish_id,
        quantity: item.quantity,
        customizations: item.customizations
      })),
      payment_method: paymentMethod
    }, {
      onSuccess: () => {
        // Reset everything on success
        setCart([]);
        setActiveTab('tracking');
        localStorage.removeItem(`cart_${qrCode}`);
        setIsProcessingOrder(false);
      },
      onError: () => {
        setIsProcessingOrder(false);
      }
    });
  };

  // Order tracking status configuration
  const getStatusIndex = (status) => {
    const statusIndex = orderStatuses.findIndex(s => s.key === status);
    return statusIndex >= 0 ? statusIndex : 0;
  };

  // Get current status progress (0 to 1)
  const getStatusProgress = (status) => {
    const currentIndex = getStatusIndex(status);
    return currentIndex / (orderStatuses.length - 1);
  };

  // Handle cancel order confirmation
  const handleCancelOrder = (order) => {
    setCancelModal({ isOpen: true, order });
  };

  const confirmCancelOrder = () => {
    if (cancelModal.order) {
      cancelOrderMutation.mutate(cancelModal.order.id);
    }
  };

  const cancelCancelOrder = () => {
    setCancelModal({ isOpen: false, order: null });
  };

  const SuggestionPopup = () => {
    if (!showSuggestionPopup) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-0">
        <div className="bg-white dark:bg-gray-800 sm:rounded-2xl rounded-xl shadow-2xl max-w-md w-full relative overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="p-5 sm:p-8 text-center border-b border-gray-100 dark:border-gray-700 flex-shrink-0 relative overflow-hidden">
            {/* Subtle background decoration */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 -z-10" />

            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-tr from-blue-600 to-blue-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
              <Sparkles className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1.5">
              {safeTranslate('customerMenu.suggestionsPopup.title', 'Recommended for You')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {safeTranslate('customerMenu.suggestionsPopup.subtitle', 'Add these popular items to complete your order')}
            </p>
          </div>

          {/* Suggested Items */}
          <div className="p-4 sm:p-6 space-y-3 overflow-y-auto flex-1 overscroll-contain">
            {suggestedDishes.map((dish) => (
              <div
                key={dish.id}
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-blue-50/50 dark:hover:bg-gray-700 transition-colors border border-gray-100 dark:border-gray-700 rounded-xl group"
              >
                <div className="flex-shrink-0 relative">
                  {dish.image_url ? (
                    <img
                      src={dish.image_url}
                      alt={dish.name}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-xl shadow-sm group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 dark:bg-gray-700 rounded-xl flex items-center justify-center shadow-sm">
                      <Utensils className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 dark:text-gray-500" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0 py-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base leading-tight mb-1 truncate">
                    {dish.name}
                  </h4>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    CHF {parseFloat(dish.price || 0).toFixed(2)}
                  </p>
                </div>

                <div className="flex-shrink-0 pl-1">
                  <button
                    onClick={() => handleAddSuggestion(dish)}
                    className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 sm:px-5 sm:py-2.5 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-transform active:scale-95 text-sm font-semibold shadow-sm flex items-center gap-1.5"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">{safeTranslate('customerMenu.suggestionsPopup.addButton', 'Add')}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
            <button
              onClick={handleSkipSuggestions}
              className="w-full py-3.5 sm:py-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {safeTranslate('customerMenu.suggestionsPopup.continueButton', 'Order')}
            </button>
            <p className="text-center text-gray-400 dark:text-gray-500 text-xs sm:text-sm mt-3 font-medium">
              {safeTranslate('customerMenu.suggestionsPopup.footerNote', 'Your order will be placed after payment')}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const OrderTrackingCard = ({ order }) => {
    const currentStatusIndex = getStatusIndex(order.status);
    const progressPercentage = getStatusProgress(order.status) * 100;

    // Safely handle items array and ensure we have proper dish names
    const orderItems = React.useMemo(() => {
      if (!Array.isArray(order.items)) return [];

      return order.items.map(item => ({
        id: item.id || item.dish_id,
        name: item.name || item.dish_name || 'Unknown Item',
        price: parseFloat(item.price) || 0,
        quantity: parseInt(item.quantity) || 1,
        customizations: item.customizations || '',
        image_url: item.image_url || null,
        // Fallback to dish data if available
        ...(item.dish || {})
      }));
    }, [order.items]);

    // Safely calculate total amount
    const totalAmount = React.useMemo(() => {
      if (order.total_amount && !isNaN(parseFloat(order.total_amount))) {
        return parseFloat(order.total_amount);
      }
      return orderItems.reduce((sum, item) => {
        return sum + (item.price * item.quantity);
      }, 0);
    }, [order.total_amount, orderItems]);

    // Get current status info
    const currentStatus = orderStatuses.find(s => s.key === order.status) || orderStatuses[0];

    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-6 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {safeTranslate('customerMenu.orderTracking.orderNumber', 'Order #{number}').replace('{number}', order.id || 'N/A')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'Date not available'} •
              {order.created_at ? new Date(order.created_at).toLocaleTimeString() : 'Time not available'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-green-600 dark:text-green-400">CHF {typeof totalAmount === 'number' ? totalAmount.toFixed(2) : '0.00'}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {safeTranslate('customerMenu.orderTracking.items', '{count} items').replace('{count}', orderItems.length.toString())}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between mb-2">
            {orderStatuses.map((status, index) => (
              <div
                key={status.key}
                className={`flex flex-col items-center transition-all duration-500 ${index <= currentStatusIndex ? 'opacity-100' : 'opacity-40'
                  }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white mb-2 transition-all duration-500 ${index < currentStatusIndex
                  ? `${status.color} scale-110`
                  : index === currentStatusIndex
                    ? `${status.color} ring-4 ${status.color.replace('bg-', 'ring-')} ring-opacity-30 scale-110`
                    : 'bg-gray-300 dark:bg-gray-600'
                  }`}>
                  <status.icon className="h-5 w-5" />
                </div>
                <span className={`text-xs font-medium text-center ${index <= currentStatusIndex ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
                  }`}>
                  {status.label}
                </span>
              </div>
            ))}
          </div>

          {/* Animated Progress Line */}
          <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-1000 ease-out"
              style={{ width: `${progressPercentage}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-pulse opacity-30"></div>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="space-y-3 mb-4">
          {orderItems.length > 0 ? (
            orderItems.map((item, index) => {
              const itemTotal = item.price * item.quantity;

              return (
                <div key={item.id || index} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                  <div className="flex items-center gap-3">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 rounded-lg flex items-center justify-center">
                        <Utensils className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Qty: {item.quantity}</p>
                      {item.customizations && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{item.customizations}</p>
                      )}
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    CHF {itemTotal.toFixed(2)}
                  </p>
                </div>
              );
            })
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              No items found for this order
            </div>
          )}
        </div>

        {/* Current Status Message */}
        <div className={`p-4 rounded-lg border ${order.status === 'cancelled'
          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          : order.status === 'completed'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${order.status === 'cancelled'
              ? 'bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-400'
              : order.status === 'completed'
                ? 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400'
                : 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400'
              }`}>
              <currentStatus.icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-sm font-semibold ${order.status === 'cancelled'
                  ? 'text-red-800 dark:text-red-300'
                  : order.status === 'completed'
                    ? 'text-green-800 dark:text-green-300'
                    : 'text-blue-800 dark:text-blue-300'
                  }`}>
                  {safeTranslate('customerMenu.orderTracking.currentStatus', 'Current Status: {status}').replace('{status}', currentStatus.label)}
                </span>
                {order.status === 'in_progress' && (
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                )}
              </div>
              <p className={`text-sm ${order.status === 'cancelled'
                ? 'text-red-600 dark:text-red-400'
                : order.status === 'completed'
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-blue-600 dark:text-blue-400'
                }`}>
                {getStatusMessage(order.status)}
              </p>
              {order.status === 'in_progress' && (
                <div className="mt-2 bg-white dark:bg-gray-800 bg-opacity-50 rounded px-3 py-1 inline-block">
                  <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                    {safeTranslate('customerMenu.orderTracking.estimatedTime', '⏱️ Estimated time: 15-20 minutes')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {order.status === 'pending' && (
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => handleCancelOrder(order)}
              disabled={cancelOrderMutation.isLoading}
              className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {cancelOrderMutation.isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{safeTranslate('customerMenu.orderTracking.cancelling', 'Cancelling...')}</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  <span>{safeTranslate('customerMenu.orderTracking.cancelOrder', 'Cancel Order')}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 text-lg font-medium">
            {safeTranslate('customerMenu.loading.title', 'Loading delicious menu...')}
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
            {safeTranslate('customerMenu.loading.description', 'Getting everything ready for you')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !menu) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900 dark:to-red-800 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Clock className="h-12 w-12 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
            {safeTranslate('customerMenu.error.title', 'Menu Not Available')}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg mb-6 leading-relaxed">
            {safeTranslate('customerMenu.error.description', 'This QR code is invalid or the menu is currently unavailable. Please check with restaurant staff.')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 transform"
          >
            {safeTranslate('customerMenu.error.returnHome', 'Return to Home')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 dark:from-gray-900 dark:to-gray-800">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10 h-full w-full bg-white dark:bg-gray-900 bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_200px,#f0f9ff,transparent)] dark:bg-[radial-gradient(circle_800px_at_50%_200px,#1e3a8a,transparent)]"></div>
      </div>

      {/* Suggestion Popup */}
      <SuggestionPopup />

      {/* Cancel Order Confirmation Modal */}
      {cancelModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full transform transition-all duration-300 scale-100">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {safeTranslate('customerMenu.orderTracking.cancelConfirm.title', 'Cancel Order')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {safeTranslate('customerMenu.orderTracking.cancelConfirm.orderNumber', 'Order #{number}').replace('{number}', cancelModal.order?.id || '')}
                  </p>
                </div>
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-6">
                {safeTranslate('customerMenu.orderTracking.cancelConfirm.message', 'Are you sure you want to cancel this order? This action cannot be undone.')}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={cancelCancelOrder}
                  disabled={cancelOrderMutation.isLoading}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
                >
                  {safeTranslate('customerMenu.orderTracking.cancelConfirm.no', 'No')}
                </button>
                <button
                  onClick={confirmCancelOrder}
                  disabled={cancelOrderMutation.isLoading}
                  className="flex-1 bg-red-600 text-white py-1 px-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {cancelOrderMutation.isLoading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{safeTranslate('customerMenu.orderTracking.cancelConfirm.cancelling', 'Cancelling...')}</span>
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4" />
                      <span>{safeTranslate('customerMenu.orderTracking.cancelConfirm.yes', 'Cancel')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-sm border-b border-gray-100 dark:border-gray-700 sticky top-0 z-50 p-3 mb-5">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                {restaurant?.logo ? (
                  <img
                    src={restaurant.logo}
                    alt={restaurant.name}
                    className="w-12 h-12 rounded-xl object-cover border border-gray-200 dark:border-gray-600"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    {restaurant?.name?.charAt(0) || 'R'}
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">{restaurant?.name}</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {safeTranslate('customerMenu.header.table', 'Table {tableNumber}').replace('{tableNumber}', table?.table_number || '')}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Tabs and Cart */}
            <div className="flex items-center gap-4">
              {/* Language Switcher */}
              <CustomerLanguageSwitcher variant="minimal" />

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </button>

              {/* Navigation Tabs */}
              <div className="hidden md:flex bg-gray-100 dark:bg-gray-700 rounded-2xl p-1">
                <button
                  onClick={() => setActiveTab('menu')}
                  className={`px-6 py-2 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'menu'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-lg'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  {safeTranslate('customerMenu.navigation.menu', 'Menu')}
                </button>
                <button
                  onClick={() => setActiveTab('tracking')}
                  className={`px-6 py-2 rounded-xl font-semibold transition-all duration-300 relative ${activeTab === 'tracking'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-lg'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  {safeTranslate('customerMenu.navigation.tracking', 'Order Tracking')}
                  {orders.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                      {orders.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Cart Button */}
              <button
                onClick={() => setShowCart(!showCart)}
                className="relative bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 group"
              >
                <ShoppingCart className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                <span className="hidden sm:inline">
                  {safeTranslate('customerMenu.navigation.myOrder', 'My Order')}
                </span>
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold border-2 border-white animate-pulse">
                    {getTotalItems()}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden mt-4 bg-gray-100 dark:bg-gray-700 rounded-2xl p-1">
            <button
              onClick={() => setActiveTab('menu')}
              className={`flex-1 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-300 ${activeTab === 'menu'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              {safeTranslate('customerMenu.navigation.menu', 'Menu')}
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`flex-1 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-300 relative ${activeTab === 'tracking'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              {safeTranslate('customerMenu.navigation.tracking', 'Tracking')}
              {orders.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                  {orders.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Order Tracking View */}
        {activeTab === 'tracking' && (
          <div className={`pb-3 max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                {safeTranslate('customerMenu.orderTracking.title', 'Your Orders')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {safeTranslate('customerMenu.orderTracking.subtitle', 'Track your food from kitchen to table')}
              </p>
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Package className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {safeTranslate('customerMenu.orderTracking.empty.title', 'No Active Orders')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                  {safeTranslate('customerMenu.orderTracking.empty.description', 'You haven\'t placed any orders yet. Browse our menu and place your first order!')}
                </p>
                <button
                  onClick={() => setActiveTab('menu')}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  {safeTranslate('customerMenu.orderTracking.empty.browseMenu', 'Browse Menu')}
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => (
                  <OrderTrackingCard key={order.id} order={order} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Menu View */}
        {activeTab === 'menu' && (
          <div className="pb-3 grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Menu Content */}
            <div className="lg:col-span-3">
              {/* Search and Filter Bar */}
              <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-8 transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Search */}
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder={safeTranslate('customerMenu.search.placeholder', 'Search dishes, ingredients...')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl text-base focus:ring-3 focus:ring-blue-500 focus:border-transparent transition-all duration-300 w-full placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Category Filter */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`px-5 py-3 rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 ${selectedCategory === null
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                        }`}
                    >
                      {safeTranslate('customerMenu.filter.allMenu', 'All Menu')}
                    </button>
                    {orderedCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`px-5 py-3 rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 ${selectedCategory === category.id
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                          }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Dishes Grid */}
              <div className="space-y-6">
                {filteredCategories
                  .filter(category => !selectedCategory || category.id === selectedCategory)
                  .map((category) => (
                    <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 px-6 py-4">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{category.name}</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {safeTranslate('customerMenu.orderTracking.items', '{count} items').replace('{count}', category.dishes.length.toString())}
                        </p>
                      </div>

                      <div className="p-6">
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                          {category.dishes.map((dish) => (
                            <div
                              key={dish.id}
                              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow h-fit bg-white dark:bg-gray-800"
                            >
                              {/* Dish Image */}
                              <div className="mb-3">
                                {dish.image_url ? (
                                  <img
                                    src={dish.image_url}
                                    alt={dish.name}
                                    className="w-full h-40 object-cover rounded-lg"
                                  />
                                ) : (
                                  <div className="w-full h-40 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                                    <Utensils className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                                  </div>
                                )}
                              </div>

                              {/* Dish Info - Compact Layout */}
                              <div className="space-y-3">
                                {/* Header with name, badges, and price */}
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-lg leading-tight mb-1">{dish.name}</h3>
                                    <div className="flex flex-wrap gap-1 mb-2">
                                      {dish.popular && (
                                        <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-2 py-1 rounded">
                                          {safeTranslate('customerMenu.dish.popular', 'Popular')}
                                        </span>
                                      )}
                                      {dish.spicy && (
                                        <span className="text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded">
                                          {safeTranslate('customerMenu.dish.spicy', 'Spicy')}
                                        </span>
                                      )}
                                      {dish.vegetarian && (
                                        <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                                          {safeTranslate('customerMenu.dish.vegetarian', 'Veg')}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-lg font-semibold text-green-600 dark:text-green-400 whitespace-nowrap">
                                    CHF {dish.price}
                                  </span>
                                </div>

                                {/* Description */}
                                {dish.description && (
                                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed line-clamp-2">
                                    {dish.description}
                                  </p>
                                )}

                                <div className="space-y-2">
                                  {/* Add to Cart Button */}
                                  <div className="flex items-center justify-between pt-1">
                                    {!dish.available ? (
                                      <span className="text-red-600 dark:text-red-400 text-sm font-medium">
                                        {safeTranslate('customerMenu.dish.soldOut', 'Sold Out')}
                                      </span>
                                    ) : (
                                      <div className="w-full">
                                        <button
                                          onClick={() => addToCart(dish)}
                                          className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                                        >
                                          <Plus className="h-4 w-4" />
                                          <span>{safeTranslate('customerMenu.dish.addToCart', 'Add to Cart')}</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              {/* Empty State */}
              {filteredCategories.length === 0 && (
                <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Search className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{safeTranslate('customerMenu.orderTracking.empty.NoDish', 'No Dishes Found')}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-lg mb-8 max-w-md mx-auto leading-relaxed">
                    {searchTerm
                      ? safeTranslate('customerMenu.orderTracking.empty.NoDishFilter', 'No dishes match your search.')
                      : safeTranslate('customerMenu.orderTracking.empty.NoDishDesc', 'No dishes available in this category at the moment.')
                    }
                  </p>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-2xl font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105"
                    >
                      Clear Search & Show All
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Cart Sidebar */}
            <div className={`mt-8 lg:mt-0 lg:col-span-1 ${showCart ? 'fixed inset-0 z-50 lg:relative lg:inset-auto' : 'hidden lg:block'}`}>
              {showCart && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
                  onClick={() => setShowCart(false)}
                />
              )}

              <div className={`bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 lg:sticky lg:top-6 flex flex-col transition-transform ${showCart ? 'fixed right-0 top-0 bottom-0 h-[100dvh] w-full max-w-md z-50 rounded-none lg:rounded-xl lg:relative lg:right-auto lg:top-auto lg:bottom-auto lg:w-auto lg:h-fit lg:max-h-[90vh] lg:max-w-none translate-x-0' : 'translate-x-full lg:translate-x-0 rounded-xl h-fit max-h-[90vh]'
                }`}>
                {/* Cart Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 rounded-t-xl flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {safeTranslate('customerMenu.navigation.myOrder', 'My Order')}
                    </h3>
                    <button
                      onClick={() => setShowCart(false)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded lg:hidden"
                    >
                      <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center">
                    <MapPin className="h-3 w-3 mr-1" />
                    {safeTranslate('customerMenu.header.table', 'Table {tableNumber}').replace('{tableNumber}', table?.table_number || '')}
                  </p>
                </div>

                {/* Cart Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4">
                  {cart.length === 0 ? (
                    <div className="text-center py-8">
                      <ShoppingCart className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">
                        {safeTranslate('customerMenu.cart.emptyTitle', 'Your cart is empty')}
                      </p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                        {safeTranslate('customerMenu.cart.emptyDescription', 'Add items to get started')}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {cart.map((item) => (
                        <div key={item.dish_id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800">
                          {/* Item Header */}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              {item.image_url ? (
                                <img
                                  src={item.image_url}
                                  alt={item.name}
                                  className="w-12 h-12 object-cover rounded flex-shrink-0"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                                  <Utensils className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <h4 className="font-medium text-gray-900 dark:text-white text-sm leading-tight truncate">
                                  {item.name}
                                </h4>
                                <p className="text-green-600 dark:text-green-400 font-semibold text-sm">CHF {item.price}</p>
                              </div>
                            </div>

                            {/* Quantity Controls */}
                            <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                              <button
                                onClick={() => updateQuantity(item.dish_id, item.quantity - 1)}
                                className="w-7 h-7 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-6 text-center font-medium text-sm text-gray-900 dark:text-white">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.dish_id, item.quantity + 1)}
                                className="w-7 h-7 bg-blue-600 text-white rounded flex items-center justify-center hover:bg-blue-700 transition-colors"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          </div>

                          {/* Customizations Display */}
                          {item.customizations && (
                            <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                              <p className="text-xs text-blue-800 dark:text-blue-300 leading-tight">{item.customizations}</p>
                            </div>
                          )}

                          {/* Edit Instructions */}
                          <div className="relative mb-2">
                            <Edit3 className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                            <input
                              type="text"
                              placeholder={safeTranslate('customerMenu.cart.instructionsPlaceholder', 'Add instructions...')}
                              value={customizationInputs[item.dish_id] || item.customizations || ''}
                              onChange={(e) => handleCustomizationChange(item.dish_id, e.target.value)}
                              className="pl-8 pr-2 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-sm w-full focus:ring-1 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                            />
                          </div>

                          {/* Remove Button */}
                          <button
                            onClick={() => removeFromCart(item.dish_id)}
                            className="w-full py-1.5 text-red-600 dark:text-red-400 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex items-center justify-center space-x-1"
                          >
                            <X className="h-3 w-3" />
                            <span>{safeTranslate('customerMenu.cart.removeItem', 'Remove Item')}</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Cart Footer - Fixed at bottom */}
                {cart.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 rounded-b-xl flex-shrink-0">
                    <div className="p-4 space-y-4">
                      {/* Total */}
                      <div className="flex justify-between items-center pt-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {safeTranslate('customerMenu.cart.total', 'Total:')}
                        </span>
                        <span className="text-xl font-bold text-green-600 dark:text-green-400">
                          CHF {getTotalPrice().toFixed(2)}
                        </span>
                      </div>

                      {/* Place Order Button */}
                      <button
                        onClick={handlePlaceOrder}
                        disabled={createOrderMutation.isLoading}
                        className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {createOrderMutation.isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-5 w-5" />
                            <span>
                              {paymentMethod === 'card' ? safeTranslate('customerMenu.cart.PayNow', 'Continue') : 'Place Order'} • CHF {getTotalPrice().toFixed(2)}
                            </span>
                          </>
                        )}
                      </button>

                      {/* Quick Actions */}
                      <div className="flex space-x-2 pt-2">
                        <button
                          onClick={() => setCart([])}
                          className="flex-1 py-2 text-gray-600 dark:text-gray-400 text-sm hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors border border-gray-300 dark:border-gray-500"
                        >
                          {safeTranslate('customerMenu.cart.clearAll', 'Clear All')}
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('tracking');
                            setShowCart(false);
                          }}
                          className="flex-1 py-2 text-blue-600 dark:text-blue-400 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors border border-blue-300 dark:border-blue-700"
                        >
                          {safeTranslate('customerMenu.cart.viewOrders', 'View Orders')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main exported component that wraps everything with the provider
const CustomerMenu = () => {
  return (
    <CustomerLanguageProvider>
      <CustomerMenuContent />
    </CustomerLanguageProvider>
  );
};

export default CustomerMenu;